/**
 * Centralized input limits and sanitization for all form fields
 * Prevents XSS, SQL injection (at client level), and enforces reasonable limits
 */

export const INPUT_LIMITS = {
  // Pet/Ad fields
  petName: 50,
  petNameMin: 2,
  adDescription: 1000,
  interestingFact: 60,
  interestingFactMinWords: 2,
  
  // Admin/Moderation fields
  rejectReason: 500,
  blockReason: 500,
  
  // Report fields
  reportReason: 500,
  reportDetails: 1000,
  
  // Chat fields
  chatMessage: 2000,
  
  // Profile fields
  displayName: 100,
  bio: 500,
  
  // Quiz fields
  quizNotes: 500,
  
  // Pet/Ad health and additional info
  healthDescription: 200,
  additionalInfo: 500,
  
  // General
  shortText: 100,
  mediumText: 500,
  longText: 2000,
} as const;

/**
 * Counts the number of words in a string
 */
export function countWords(value: string): number {
  const trimmed = value.trim();
  if (!trimmed) return 0;
  return trimmed.split(/\s+/).filter(Boolean).length;
}

/**
 * Validates that a string has at least minLength characters (after trim)
 */
export function validateMinLength(value: string, minLength: number): boolean {
  return value.trim().length >= minLength;
}

/**
 * Validates that a string has at least minWords words
 */
export function validateMinWords(value: string, minWords: number): boolean {
  return countWords(value) >= minWords;
}

/**
 * Sanitizes input to prevent XSS and remove dangerous content
 * Should be used on all user inputs before saving
 * @param value - The input value to sanitize
 * @param trimEnds - Whether to trim whitespace from ends (default: false for onChange, true for final validation)
 */
export function sanitizeInput(value: string, trimEnds = false): string {
  if (!value) return '';
  
  let result = value
    // Remove HTML tags
    .replace(/<[^>]*>/g, '')
    // Remove potential XSS patterns
    .replace(/javascript:/gi, '')
    .replace(/on\w+\s*=/gi, '')
    .replace(/data:/gi, '')
    // Remove control characters (except newlines and tabs for textareas)
    .replace(/[\x00-\x08\x0B\x0C\x0E-\x1F\x7F]/g, '')
    // Normalize multiple spaces to single (but preserve leading/trailing for typing)
    .replace(/  +/g, ' ');
  
  return trimEnds ? result.trim() : result;
}

/**
 * Sanitizes input for single-line fields (no newlines allowed)
 * @param trimEnds - Whether to trim whitespace from ends (default: false for onChange)
 */
export function sanitizeInputSingleLine(value: string, trimEnds = false): string {
  let result = sanitizeInput(value, trimEnds);
  // Replace all newline variants with single space, then normalize multiple spaces
  result = result.replace(/[\r\n]+/g, ' ').replace(/  +/g, ' ');
  return result;
}

/**
 * Sanitizes input for multi-line fields (preserves newlines)
 * @param trimEnds - Whether to trim whitespace from ends (default: false for onChange)
 */
export function sanitizeInputMultiLine(value: string, trimEnds = false): string {
  if (!value) return '';
  
  let result = value
    // Remove HTML tags
    .replace(/<[^>]*>/g, '')
    // Remove potential XSS patterns
    .replace(/javascript:/gi, '')
    .replace(/on\w+\s*=/gi, '')
    .replace(/data:/gi, '')
    // Remove control characters (keep newlines \n and \r)
    .replace(/[\x00-\x08\x0B\x0C\x0E-\x1F\x7F]/g, '')
    // Normalize multiple newlines to max 2
    .replace(/\n{3,}/g, '\n\n');
  
  return trimEnds ? result.trim() : result;
}

/**
 * Creates an onChange handler that enforces maxLength and sanitizes input
 * Note: Does NOT trim during typing - trim should happen on form submission
 */
export function createSanitizedHandler(
  setter: (value: string) => void,
  maxLength: number,
  multiLine = false
): (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => void {
  return (e) => {
    let value = e.target.value;
    
    // Enforce max length first
    if (value.length > maxLength) {
      value = value.slice(0, maxLength);
    }
    
    // Sanitize WITHOUT trimming (user is still typing)
    value = multiLine ? sanitizeInputMultiLine(value, false) : sanitizeInputSingleLine(value, false);
    
    setter(value);
  };
}
